import { useEffect, useState } from "react";
import styled from "styled-components";
import { Modal, Table, Upload } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { showErrorNotification } from "src/util/notifications";
import { useMsal } from "@azure/msal-react";
import * as XLSX from 'xlsx';
import { cleanImportLayout, fetchLocationListUpdateSummary, importFile } from "./actions";
import Button from "src/components/common/Button/Button";
import { usePDF } from "react-to-pdf";
const { Dragger } = Upload;

export const LocationListUploadModal = ({
  visible,
  onCancel,
  stateKey,
  params,
  confirmLoading,
  setConfirmLoading,
}: any) => {

  const dispatch = useDispatch();
  const isImportLoading = useSelector(
    (state: any) => state[stateKey]
  )?.importLoading;

  const { accounts }: any = useMsal();
  const [open, setOpen] = useState(false);
  const [fileData, setFileData] = useState<(string | number | undefined)[][] | null>(null); // Store the parsed data from the excel file
  const [isModalVisible, setIsModalVisible] = useState(false); // Control modal visibility
  const [fileOptions, setFileOptions] = useState();
  const [messageText, setMessageText] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [importedData, setData] = useState<string | null>(null);
  const [errorModal, setErrorModal] = useState(false);
  const [fileList, setFileList] = useState([]);
  const { toPDF, targetRef } = usePDF({ filename: "ValidationReport.pdf" });

  const handleUpload = (file: any) => {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as (string | number | undefined)[][];
      setFileData(jsonData);
    };
    reader.readAsBinaryString(file);
    return false;
  };

  const handleChanges = (options: any) => {
    setMessageText("");
    const format = options?.file?.name?.split(".").pop();
    if (format === "xlsx") {
      if (options?.file?.status !== "removed") {
        setFileOptions(options.file);
        setFileList(options?.fileList);
        setOpen(true);
      }
    } else {
      showErrorNotification(
        "Incorrect file format. Only .xlsx files are supported.");
    }
  };

  const handleConfirm = () => {
    setConfirmLoading(true);
    dispatch(
      importFile({
        file: fileOptions,
        params: params,
        UserName: accounts?.[0]?.username,
      })
    );
    dispatch(cleanImportLayout());
    setIsModalVisible(false);
    setTimeout(() => {
      setShowPopup(false);
      handleCancel();
      setOpen(false);
      setIsModalVisible(false);
      setConfirmLoading(false);
      setFileData(null);
      dispatch(fetchLocationListUpdateSummary({}));
      onCancel();
    }, 100);
  };


  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setFileList([]);
    dispatch(cleanImportLayout());
    setIsModalVisible(false);
    setFileData(null);
  };

  const handleClose = () => {
    setMessageText("");
    setFileList([]);
    onCancel();
  };

  let importData = useSelector(
    (state: any) => state[stateKey]
  ).importValidationData;

  useEffect(() => {
    try {
      if (typeof importData === "string") {
        dispatch(fetchLocationListUpdateSummary({}));
        handleCancel();
        setShowPopup(true);
        setConfirmLoading(false)
        setData(importData);
      } else if (Object.keys(importData)?.length) {
        setErrorModal(true);
        dispatch(fetchLocationListUpdateSummary({}));
      }
    } catch (error: any) {
      if (error && error?.response) {
        if (error?.response?.data) {
          showErrorNotification(error.response.data.error.messages[0]);
        } else {
          showErrorNotification(error.response);
        }
      }
    }
  }, [importData]);

  const columns = fileData ? fileData[0].map((col: any, index: any) => ({
    title: col,
    dataIndex: index,
    key: index,
  })) : [];

  const data = fileData ? fileData.slice(1).map((row, index) => {
    if (!row[0]) {
      return;
    }

    const rowData = row.reduce<{ [key: number]: any }>((acc: any, cell: any, idx: any) => {
      acc[idx] = cell;
      return acc;
    }, {});
    return { key: index, ...rowData };
  }).filter(Boolean) : [];


  return (
    <StyledImportModal
      title="Select an excel file(.xlsx) for Location List Update"
      open={visible}
      onCancel={handleClose}
      footer={null}
      centered
    >
      <Dragger
        name="file"
        multiple={false}
        maxCount={1}
        showUploadList={true}
        beforeUpload={handleUpload}
        fileList={fileList}
        onChange={handleChanges}
        accept=".xlsx,.xls"
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        {isImportLoading ? (
          <p className="ant-upload-hint">Processing...</p>
        ) : (
          <>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">Support for a single file only</p>
          </>
        )}
      </Dragger>
      <Button
        size="middle"
        onClick={showModal}
        style={{ marginRight: "10px", marginTop: "10px" }}
        disabled={fileList.length === 0}
      >
        Location List Summary
      </Button>
      <Modal
        title={<span style={{ marginLeft: '20px' }}>Location List Summary</span>}
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button key="back" size="middle" style={{ marginRight: "10px", marginBottom: "5px" }} onClick={handleCancel}>
              Cancel
            </Button>
            <Button key="confirm" size="middle" style={{ marginRight: "10px", marginBottom: "5px" }} onClick={handleConfirm}>
              Confirm
            </Button>
          </div>
        ]}
        confirmLoading={confirmLoading}
        width={800}
      >
        {fileData && (
          <Table
            columns={columns}
            dataSource={data}
            pagination={false}
            size="middle"
          />
        )}
      </Modal>
    </StyledImportModal>
  );
};

const StyledImportModal = styled(Modal)`
    .ant-modal-content {
      width: initial;
    }
    `;
